import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { OPENING_HOUR_WEEK_DAY_OPTIONS, WORKING_DAYS_WEEK_DAY } from "../../../utils";

const COUNTRY_CHOICES = [{ value: "PL", label: "Poland" }];

export interface OpeningHourRow {
  id?: string;
  week_day: string;
  from_hour: string;
  to_hour: string;
}

export interface AddressFormData {
  country: string;
  city: string;
  street: string;
  zip_code: string;
  phone_number: string;
  email: string;
  notes: string;
  opening_hours?: OpeningHourRow[];
}

export const defaultAddressFormData: AddressFormData = {
  country: "PL",
  city: "",
  street: "",
  zip_code: "",
  phone_number: "",
  email: "",
  notes: "",
  opening_hours: [],
};

interface AddressFormProps {
  value: AddressFormData;
  onChange: (data: AddressFormData) => void;
  errors?: Partial<Record<keyof AddressFormData, string>>;
  /** Fields that are required (shows * indicator). */
  required?: Array<keyof AddressFormData>;
  /** When true the email, phone_number, notes fields are hidden. */
  compact?: boolean;
}

export default function AddressForm({
  value,
  onChange,
  errors,
  required = [],
  compact = false,
}: AddressFormProps) {
  const { t } = useTranslation();

  const update = (field: keyof AddressFormData, fieldValue: string | OpeningHourRow[]) =>
    onChange({ ...value, [field]: fieldValue });

  const isRequired = (field: keyof AddressFormData) => required.includes(field);

  const addOpeningHour = () => {
    const currentHours = value.opening_hours || [];
    update("opening_hours", [
      ...currentHours,
      { week_day: WORKING_DAYS_WEEK_DAY, from_hour: "08:00", to_hour: "17:00" },
    ]);
  };

  const removeOpeningHour = (index: number) => {
    const currentHours = value.opening_hours || [];
    update("opening_hours", currentHours.filter((_, i) => i !== index));
  };

  const updateOpeningHour = (index: number, field: keyof OpeningHourRow, fieldValue: string) => {
    const currentHours = value.opening_hours || [];
    const updated = [...currentHours];
    updated[index] = { ...updated[index], [field]: fieldValue };
    update("opening_hours", updated);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
        <Box sx={{ flex: { xs: "1 1 100%", sm: 2 } }}>
          <TextField
            size="small"
            label={t("Street")}
            value={value.street}
            onChange={(e) => update("street", e.target.value)}
            fullWidth
            required={isRequired("street")}
            error={!!errors?.street}
            helperText={errors?.street}
          />
        </Box>
        <Box sx={{ flex: { xs: "1 1 100%", sm: 1 } }}>
          <TextField
            size="small"
            label={t("Zip code")}
            value={value.zip_code}
            onChange={(e) => update("zip_code", e.target.value)}
            fullWidth
            required={isRequired("zip_code")}
            error={!!errors?.zip_code}
            helperText={errors?.zip_code}
          />
        </Box>
        <Box sx={{ flex: { xs: "1 1 100%", sm: 1 } }}>
          <TextField
            size="small"
            label={t("City")}
            value={value.city}
            onChange={(e) => update("city", e.target.value)}
            fullWidth
            required={isRequired("city")}
            error={!!errors?.city}
            helperText={errors?.city}
          />
        </Box>
        <Box sx={{ flex: { xs: "1 1 100%", sm: 1 } }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Country")}</InputLabel>
            <Select
              label={t("Country")}
              value={value.country}
              onChange={(e) => update("country", e.target.value)}
              required={isRequired("country")}
            >
              {COUNTRY_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {t(c.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {!compact && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <TextField
            size="small"
            label={t("Phone number")}
            value={value.phone_number}
            onChange={(e) => update("phone_number", e.target.value)}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />
          <TextField
            size="small"
            label={t("Email")}
            type="email"
            value={value.email}
            onChange={(e) => update("email", e.target.value)}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />
          <TextField
            size="small"
            label={t("Notes")}
            value={value.notes}
            onChange={(e) => update("notes", e.target.value)}
            sx={{ flex: { xs: "1 1 100%", sm: 2 } }}
          />
        </Box>
      )}

      {/* Opening hours section */}
      <Box sx={{ p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: "var(--textColor)" }}>
          {t("Opening hours")}
        </Typography>
        {(value.opening_hours || []).map((oh, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #eee",
              borderRadius: 1,
              p: 2,
              mb: 1,
              backgroundColor: "var(--componentBgColor)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                {t("Opening hours")} #{index + 1}
              </Typography>
              <IconButton size="small" color="error" onClick={() => removeOpeningHour(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  width: "100%",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 160, "& .MuiInputBase-root": { color: "var(--textColor)" } }}>
                    <InputLabel>{t("Week day")}</InputLabel>
                    <Select
                      label={t("Week day")}
                      value={oh.week_day}
                      onChange={(e) => updateOpeningHour(index, "week_day", e.target.value)}
                    >
                      {OPENING_HOUR_WEEK_DAY_OPTIONS.map((wd) => (
                        <MenuItem key={wd.value} value={wd.value}>
                          {t(wd.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                  <TimeField
                    size="small"
                    label={t("From")}
                    value={oh.from_hour ? dayjs(oh.from_hour, "HH:mm") : null}
                    onChange={(value: Dayjs | null) =>
                      updateOpeningHour(index, "from_hour", value ? value.format("HH:mm") : "08:00")
                    }
                    format="HH:mm"
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { color: "var(--textColor)" } }}
                  />
                </Box>
                <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                  <TimeField
                    size="small"
                    label={t("To")}
                    value={oh.to_hour ? dayjs(oh.to_hour, "HH:mm") : null}
                    onChange={(value: Dayjs | null) =>
                      updateOpeningHour(index, "to_hour", value ? value.format("HH:mm") : "17:00")
                    }
                    format="HH:mm"
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { color: "var(--textColor)" } }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
        <Button startIcon={<Add />} variant="outlined" size="small" onClick={addOpeningHour}>
          {t("Add")} {t("Opening hours")}
        </Button>
      </Box>
    </Box>
  );
}
